import type {
  SettingsArchetype,
  SettingsAuditPayload,
  SettingsIssueGroup,
  RestaurantSettingsProfile,
} from '../types';

/** Merge per-scope settings audits into one matrix for comparison mode. */
export function mergeSettingsAudits(
  audits: Array<SettingsAuditPayload | null | undefined>,
): SettingsAuditPayload | null {
  const present = audits.filter(
    (a): a is SettingsAuditPayload => !!a && a.restaurants.length > 0,
  );
  if (present.length === 0) return null;
  if (present.length === 1) return present[0];

  const seenOutlets = new Set<string>();
  const restaurants: RestaurantSettingsProfile[] = [];
  for (const audit of present) {
    for (const r of audit.restaurants) {
      if (seenOutlets.has(r.outletId)) continue;
      seenOutlets.add(r.outletId);
      restaurants.push(r);
    }
  }

  const restaurantIds = new Set(restaurants.map(r => r.outletId));
  const archById = new Map<string, SettingsArchetype>();
  for (const audit of present) {
    for (const a of audit.archetypes) {
      const existing = archById.get(a.id);
      if (!existing) {
        archById.set(a.id, { ...a, outletIds: [...a.outletIds] });
      } else {
        existing.outletIds = [
          ...new Set([...existing.outletIds, ...a.outletIds]),
        ];
      }
    }
  }
  const archetypes = [...archById.values()]
    .map(a => ({
      ...a,
      outletIds: a.outletIds.filter(id => restaurantIds.has(id)),
    }))
    .filter(a => a.outletIds.length > 0);

  const issuesById = new Map<string, SettingsIssueGroup>();
  for (const audit of present) {
    for (const g of audit.issueGroups) {
      const existing = issuesById.get(g.id);
      if (!existing) {
        issuesById.set(g.id, { ...g, affected: [...g.affected] });
        continue;
      }
      const seen = new Set(
        existing.affected.map(
          a => `${a.outletId}|${a.shiftName ?? ''}|${a.detail ?? ''}`,
        ),
      );
      for (const inst of g.affected) {
        const key = `${inst.outletId}|${inst.shiftName ?? ''}|${inst.detail ?? ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        existing.affected.push(inst);
      }
    }
  }

  return {
    columns: present[0].columns,
    archetypes,
    restaurants,
    // Per-scope medians are not meaningful once outlets are unioned.
    benchmarks: [],
    issueGroups: [...issuesById.values()],
    outletsChecked: restaurants.length,
    shiftsChecked: restaurants.reduce((sum, r) => sum + r.shiftCount, 0),
  };
}
